import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, MapPin, Phone, Mail, Globe, Star, Briefcase, Award, Clock, Facebook, Instagram, Linkedin, Twitter } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatPhone = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
  return phone;
};

export default function PublicProviderView() {
  const { providerId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API}/public/provider/${providerId}`);
        setData(res.data);
      } catch {
        toast.error('Profile not found');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [providerId]);

  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" /></div>;
  if (!data) return <div className="flex flex-col items-center justify-center min-h-screen gap-4"><p className="text-lg text-stone-600">Profile not found</p><Button onClick={() => navigate(-1)} variant="outline">Go Back</Button></div>;

  const p = data.profile || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100">
      <div className="bg-white border-b border-stone-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button onClick={() => navigate(-1)} variant="ghost" size="sm" className="text-stone-600 hover:text-purple-700" data-testid="back-button">
            <ArrowLeft size={18} className="mr-2" /> Back
          </Button>
          <h1 className="text-xl md:text-2xl font-bold text-purple-700" style={{ fontFamily: '"Playfair Display", serif' }}>Service Provider</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="provider-public-view">
        {/* Hero Card */}
        <Card className="p-6 mb-6">
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <div className="w-24 h-24 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold text-3xl flex-shrink-0">
              {p.photo_url ? <img src={p.photo_url} alt={data.name} className="w-24 h-24 rounded-full object-cover" /> : data.name?.charAt(0)}
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-stone-900" data-testid="provider-name">{data.name}</h2>
              {data.company_name && <p className="text-stone-600">{data.company_name}</p>}
              {data.service_type && <span className="inline-block mt-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">{data.service_type}</span>}
              <div className="flex flex-wrap gap-4 mt-3 text-sm text-stone-700">
                {(data.city || p.city) && (
                  <span className="flex items-center gap-1"><MapPin size={15} className="text-purple-600" />{data.city || p.city}, {data.state || p.state}</span>
                )}
                {(data.email || p.email) && <span className="flex items-center gap-1"><Mail size={15} className="text-purple-600" />{data.email || p.email}</span>}
                {(data.phone || p.phone) && <span className="flex items-center gap-1"><Phone size={15} className="text-purple-600" />{formatPhone(data.phone || p.phone)}</span>}
              </div>
              {data.average_rating > 0 && (
                <div className="flex items-center gap-1 mt-2 text-sm"><Star size={16} className="text-yellow-500 fill-yellow-500" /><span className="font-semibold">{data.average_rating}</span><span className="text-stone-500">({data.total_reviews} reviews)</span></div>
              )}
            </div>
          </div>
        </Card>

        {/* Bio */}
        {p.bio && (
          <Card className="p-6 mb-6">
            <h3 className="font-semibold text-lg text-stone-900 mb-2">About</h3>
            <p className="text-stone-700 whitespace-pre-line">{p.bio}</p>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Details */}
          <Card className="p-6">
            <h3 className="font-semibold text-lg text-stone-900 mb-3">Details</h3>
            <div className="space-y-2 text-sm text-stone-700">
              {p.years_of_experience && <p><span className="font-medium">Experience:</span> {p.years_of_experience} years</p>}
              {p.hourly_rate && <p><span className="font-medium">Rate:</span> {p.hourly_rate}</p>}
              <p className="flex items-center gap-1.5">
                <Clock size={14} className="text-purple-600" />
                <span className="font-medium">Business Hours:</span> {p.business_hours || 'Mon-Fri: 9AM-5PM'}
              </p>
              {p.website && (
                <a href={p.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-purple-600 hover:underline"><Globe size={14} />{p.website}</a>
              )}
              
            </div>
          </Card>

          {/* Service Areas */}
          {p.service_areas && p.service_areas.length > 0 && (
            <Card className="p-6">
              <h3 className="font-semibold text-lg text-stone-900 mb-3">Service Areas</h3>
              <div className="flex flex-wrap gap-2">
                {p.service_areas.map((a, i) => (
                  <span key={i} className="px-3 py-1 bg-stone-100 text-stone-700 rounded-full text-sm">{a.city}, {a.state}</span>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Connect: Social Media & External Reviews */}
        {(p.external_reviews_url || (p.social_media && (p.social_media.facebook || p.social_media.instagram || p.social_media.linkedin || p.social_media.twitter))) && (
          <Card className="p-6 mb-6" data-testid="social-media-section">
            <h3 className="font-semibold text-lg text-stone-900 mb-3">Connect</h3>
            <div className="flex flex-wrap gap-3">
              {p.social_media?.facebook && (
                <a href={`https://facebook.com/${p.social_media.facebook}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium" data-testid="social-facebook">
                  <Facebook size={18} /> Facebook
                </a>
              )}
              {p.social_media?.instagram && (
                <a href={`https://instagram.com/${p.social_media.instagram}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-pink-50 text-pink-700 rounded-lg hover:bg-pink-100 transition-colors text-sm font-medium" data-testid="social-instagram">
                  <Instagram size={18} /> Instagram
                </a>
              )}
              {p.social_media?.linkedin && (
                <a href={`https://linkedin.com/in/${p.social_media.linkedin}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-sky-50 text-sky-700 rounded-lg hover:bg-sky-100 transition-colors text-sm font-medium" data-testid="social-linkedin">
                  <Linkedin size={18} /> LinkedIn
                </a>
              )}
              {p.social_media?.twitter && (
                <a href={`https://twitter.com/${p.social_media.twitter}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-stone-100 text-stone-700 rounded-lg hover:bg-stone-200 transition-colors text-sm font-medium" data-testid="social-twitter">
                  <Twitter size={18} /> X (Twitter)
                </a>
              )}
              {p.external_reviews_url && (
                <a href={p.external_reviews_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors text-sm font-medium" data-testid="social-external-reviews">
                  <Star size={18} className="fill-amber-400 text-amber-400" /> External Reviews
                </a>
              )}
            </div>
          </Card>
        )}

        {/* Service Types */}
        {data.service_types && data.service_types.length > 0 && (
          <Card className="p-6 mb-6">
            <h3 className="font-semibold text-lg text-stone-900 mb-3 flex items-center gap-2"><Briefcase size={18} />Services Offered</h3>
            <div className="flex flex-wrap gap-2">
              {data.service_types.map((t, i) => <span key={i} className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-sm font-medium">{t}</span>)}
            </div>
          </Card>
        )}

        {/* Certifications */}
        {p.certifications && p.certifications.length > 0 && (
          <Card className="p-6 mb-6">
            <h3 className="font-semibold text-lg text-stone-900 mb-3 flex items-center gap-2"><Award size={18} />Certifications</h3>
            <div className="flex flex-wrap gap-2">
              {p.certifications.map((c, i) => <span key={i} className="px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-sm">{c}</span>)}
            </div>
          </Card>
        )}

        {/* Reviews */}
        <Card className="p-6 mb-6" data-testid="reviews-section">
          <h3 className="font-semibold text-lg text-stone-900 mb-4 flex items-center gap-2">
            <Star size={18} />Customer Reviews {data.reviews && data.reviews.length > 0 && `(${data.reviews.length})`}
          </h3>
          {data.reviews && data.reviews.length > 0 ? (
            <div className="space-y-4">
              {data.reviews.map((r, i) => (
                <div key={i} className="border-b border-stone-100 pb-4 last:border-0">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className="flex">{Array.from({ length: 5 }).map((_, si) => <Star key={si} size={14} className={si < r.rating ? 'text-yellow-500 fill-yellow-500' : 'text-stone-300'} />)}</div>
                      <span className="font-medium text-sm text-stone-800">{r.reviewer_name}</span>
                    </div>
                    {r.date_of_service && <span className="text-xs text-stone-400">{r.date_of_service}</span>}
                  </div>
                  {r.service_type && <span className="inline-block text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded mb-1">{r.service_type}</span>}
                  {r.review_text && <p className="text-sm text-stone-700 mt-1">{r.review_text}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-stone-500">No reviews yet.</p>
          )}
        </Card>
      </div>
    </div>
  );
}
